const MIKO_SIZE_INDEX = {
  "X-Small": 1, XS: 1,
  Small: 2, S: 2,
  Medium: 3, M: 3,
  Large: 4, L: 4,
  "X-Large": 5, XL: 5
};

function mikoCartRoutes() {
  const routes = (window.theme && window.theme.routes && window.theme.routes.cart) || {};
  return {
    add: `${routes.add || "/cart/add"}.js`,
    change: `${routes.change || "/cart/change"}.js`,
    cart: `${routes.base || "/cart"}.js`,
    page: routes.base || "/cart"
  };
}

async function mikoJsonFetch(url, options = {}) {
  const response = await fetch(url, { credentials: "same-origin", ...options });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status === 422) {
    throw new Error(data.description || data.message || "Unable to update the cart.");
  }
  return data;
}

async function mikoRefreshCart({ open = false, flash = null } = {}) {
  if (window.IraMiko && typeof window.IraMiko.refreshCart === "function") {
    return window.IraMiko.refreshCart({ open, flash });
  }
  if (open) window.location.href = mikoCartRoutes().page;
  return null;
}

function mikoShowFormError(form, message) {
  const error = form.querySelector("[data-error-message]");
  if (error) error.textContent = message || "Unable to add this set to the cart.";
  document.dispatchEvent(new CustomEvent("flu:cart:error", { detail: { errorMessage: message } }));
}

function mikoSetSizingInfo(wrapper, value) {
  if (!wrapper) return;
  const index = MIKO_SIZE_INDEX[value] || 0;
  wrapper.querySelectorAll("[data-miko-size-index]").forEach((item) => {
    item.classList.toggle("is-active", Number(item.dataset.mikoSizeIndex) === index);
  });
}

function mikoUpdateRegularSizing(select) {
  const position = select.id && select.id.match(/Option(\d+)/)?.[1];
  const scope = select.closest(".product-form-variant-block") || select.closest("form") || document;
  const wrappers = position
    ? scope.querySelectorAll(`[data-miko-sizing-info][data-miko-size-option-position="${position}"]`)
    : scope.querySelectorAll("[data-miko-sizing-info]");
  wrappers.forEach((wrapper) => mikoSetSizingInfo(wrapper, select.value));
}

document.addEventListener("change", (event) => {
  const select = event.target.closest('select[name^="options["]');
  if (select) mikoUpdateRegularSizing(select);
});

document.addEventListener("flu:product:variant-change", () => {
  document.querySelectorAll('select[name^="options["]').forEach(mikoUpdateRegularSizing);
});

// Mark size chips that are unavailable for the currently selected colour/length.
// The chips remain selectable so shoppers can see the sold-out state, but a
// diagonal line makes the unavailable sizes immediately clear.
const MIKO_PRODUCT_DATA_CACHE = new Map();

function mikoProductDataUrl(handle) {
  const base = (window.theme && window.theme.routes && window.theme.routes.products) || "/products";
  return `${base}/${handle}.js`;
}

async function mikoGetProductData(form) {
  const handle = form && form.dataset && form.dataset.productHandle;
  if (!handle) return null;
  if (!MIKO_PRODUCT_DATA_CACHE.has(handle)) {
    MIKO_PRODUCT_DATA_CACHE.set(
      handle,
      fetch(mikoProductDataUrl(handle), { credentials: "same-origin" })
        .then((response) => {
          if (!response.ok) throw new Error("Unable to load product availability.");
          return response.json();
        })
        .catch(() => null)
    );
  }
  return MIKO_PRODUCT_DATA_CACHE.get(handle);
}

function mikoSelectedProductOptions(form) {
  const selected = {};
  form.querySelectorAll('select[name^="options["]').forEach((select) => {
    const match = (select.id || "").match(/Option(\d+)/);
    if (match) selected[Number(match[1])] = select.value;
  });
  return selected;
}

function mikoApplySizeAvailability(form, productData) {
  if (!form || !productData || !Array.isArray(productData.variants)) return;
  const selected = mikoSelectedProductOptions(form);

  form.querySelectorAll("[data-miko-size-option-button]").forEach((button) => {
    const position = Number(button.dataset.mikoOptionPosition);
    const value = button.dataset.mikoOptionValue || "";
    const optionIndex = position - 1;

    const available = productData.variants.some((variant) => {
      if (!variant.available || variant.options[optionIndex] !== value) return false;
      return variant.options.every((variantValue, index) => {
        const otherPosition = index + 1;
        return otherPosition === position || !selected[otherPosition] || selected[otherPosition] === variantValue;
      });
    });

    button.classList.toggle("is-unavailable", !available);
    button.dataset.mikoUnavailable = String(!available);
    if (!button.dataset.mikoBaseLabel) {
      button.dataset.mikoBaseLabel = button.getAttribute("aria-label") || value;
    }
    button.setAttribute(
      "aria-label",
      available ? button.dataset.mikoBaseLabel : `${button.dataset.mikoBaseLabel} — sold out`
    );
  });
}

async function mikoInitSizeAvailability(root = document) {
  const forms = root.matches && root.matches("[data-product-form]")
    ? [root]
    : [...root.querySelectorAll("[data-product-form]")];

  await Promise.all(forms.map(async (form) => {
    if (!form.querySelector("[data-miko-size-option-button]")) return;
    const productData = await mikoGetProductData(form);
    if (productData) mikoApplySizeAvailability(form, productData);
  }));
}

document.addEventListener("change", (event) => {
  const select = event.target.closest('select[name^="options["]');
  if (!select) return;
  const form = select.closest("[data-product-form]");
  if (!form) return;
  window.setTimeout(async () => {
    const productData = await mikoGetProductData(form);
    if (productData) mikoApplySizeAvailability(form, productData);
  }, 0);
});

function mikoUpdateBundleProduct(fieldset) {
  const script = fieldset.querySelector("[data-miko-bundle-json]");
  if (!script) return;
  let product;
  try { product = JSON.parse(script.textContent); } catch (_) { return; }
  const selected = [...fieldset.querySelectorAll("[data-miko-bundle-option]")]
    .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
    .map((select) => select.value);
  const variant = product.variants.find((candidate) =>
    candidate.options.every((option, index) => option === selected[index])
  );
  const available = Boolean(variant && variant.available);
  fieldset.dataset.selectedVariantId = available ? String(variant.id) : "";
  const warning = fieldset.querySelector("[data-miko-bundle-unavailable]");
  if (warning) warning.hidden = available;
  fieldset.querySelectorAll("[data-miko-bundle-option]").forEach((select) => {
    const wrapper = fieldset.querySelector(`[data-miko-sizing-info][data-miko-size-option-position="${select.dataset.optionPosition}"]`);
    if (wrapper) mikoSetSizingInfo(wrapper, select.value);
  });
  const form = fieldset.closest("form");
  const button = form && form.querySelector("[data-miko-bundle-add]");
  if (button) {
    const allAvailable = [...form.querySelectorAll("[data-miko-bundle-product]")]
      .every((item) => Boolean(item.dataset.selectedVariantId));
    button.disabled = !allAvailable;
    button.setAttribute("aria-disabled", String(!allAvailable));
  }
}

function mikoInitBundles(root = document) {
  root.querySelectorAll("[data-miko-bundle-product]").forEach(mikoUpdateBundleProduct);
}

document.addEventListener("change", (event) => {
  const select = event.target.closest("[data-miko-bundle-option]");
  if (select) mikoUpdateBundleProduct(select.closest("[data-miko-bundle-product]"));
});

document.addEventListener("submit", async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.querySelector("[data-miko-bundle-products]")) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  const button = form.querySelector("[data-miko-bundle-add]");
  const quantity = Math.max(1, Number(form.querySelector('[name="quantity"]')?.value || 1));
  const items = [...form.querySelectorAll("[data-miko-bundle-product]")].map((item) => ({
    id: Number(item.dataset.selectedVariantId),
    quantity
  }));
  if (!items.length || items.some((item) => !item.id)) {
    mikoShowFormError(form, "Please select an available option for every item.");
    return;
  }
  button?.classList.add("loading");
  if (button) button.disabled = true;
  try {
    const result = await mikoJsonFetch(mikoCartRoutes().add, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify({ items })
    });
    const first = result.items && result.items[0];
    document.dispatchEvent(new CustomEvent("flu:cart:item-added", { detail: { product: first || result } }));
    await mikoRefreshCart({ open: document.body.dataset.enableQuickCart === "true", flash: first && first.variant_id });
    if (document.body.dataset.enableQuickCart !== "true") window.location.href = mikoCartRoutes().page;
  } catch (error) {
    mikoShowFormError(form, error.message);
  } finally {
    button?.classList.remove("loading");
    mikoInitBundles(form);
  }
}, true);

async function mikoAddCardVariant(button) {
  if (button.disabled) return;
  button.classList.add("is-loading");
  button.disabled = true;
  try {
    const result = await mikoJsonFetch(mikoCartRoutes().add, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1 }] })
    });
    const item = result.items && result.items[0];
    document.dispatchEvent(new CustomEvent("flu:cart:item-added", { detail: { product: item || result } }));
    await mikoRefreshCart({ open: document.body.dataset.enableQuickCart === "true", flash: item && item.variant_id });
    if (document.body.dataset.enableQuickCart !== "true") window.location.href = mikoCartRoutes().page;
  } catch (error) {
    document.dispatchEvent(new CustomEvent("flu:cart:error", { detail: { errorMessage: error.message } }));
    window.alert(error.message);
  } finally {
    button.classList.remove("is-loading");
    button.disabled = false;
  }
}

document.addEventListener("click", (event) => {
  const button = event.target.closest("[data-miko-card-add]");
  if (button) {
    event.preventDefault();
    mikoAddCardVariant(button);
  }
});

function mikoSyncGiftWrap(cart) {
  if (!cart || !Array.isArray(cart.items)) return;
  document.querySelectorAll("[data-miko-gift-wrap]").forEach((checkbox) => {
    const id = Number(checkbox.dataset.variantId);
    checkbox.checked = cart.items.some((item) => Number(item.variant_id) === id);
  });
}

document.addEventListener("change", async (event) => {
  const checkbox = event.target.closest("[data-miko-gift-wrap]");
  if (!checkbox) return;
  checkbox.disabled = true;
  const id = Number(checkbox.dataset.variantId);
  try {
    if (checkbox.checked) {
      await mikoJsonFetch(mikoCartRoutes().add, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ items: [{ id, quantity: 1 }] })
      });
    } else {
      await mikoJsonFetch(mikoCartRoutes().change, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ id, quantity: 0 })
      });
    }
    const cart = await mikoRefreshCart();
    mikoSyncGiftWrap(cart);
    if (document.body.classList.contains("template-cart")) window.location.reload();
  } catch (error) {
    checkbox.checked = !checkbox.checked;
    window.alert(error.message);
  } finally {
    checkbox.disabled = false;
  }
});

document.addEventListener("flu:cart:updated", (event) => mikoSyncGiftWrap(event.detail && event.detail.cart));

const MIKO_VIEW_KEY = "miko_collection_view_mode";
function mikoApplyViewMode(mode) {
  const safeMode = mode === "item" ? "item" : "model";
  document.documentElement.dataset.mikoViewMode = safeMode;
  try { localStorage.setItem(MIKO_VIEW_KEY, safeMode); } catch (_) {}
  document.querySelectorAll("[data-miko-view-mode-toggle]").forEach((button) => {
    const label = safeMode === "item" ? button.dataset.itemLabel : button.dataset.modelLabel;
    if (button.textContent !== label) button.textContent = label;
    button.setAttribute("aria-pressed", String(safeMode === "item"));
  });
}

document.addEventListener("click", (event) => {
  const button = event.target.closest("[data-miko-view-mode-toggle]");
  if (!button) return;
  event.preventDefault();
  mikoApplyViewMode(document.documentElement.dataset.mikoViewMode === "item" ? "model" : "item");
});

async function mikoFilterGallery(select) {
  const container = select.closest(".product__media-container");
  const slider = container && container.querySelector("[data-slider]");
  if (!slider) return;
  if (!slider._mikoAllSlides) {
    slider._mikoAllSlides = [...slider.querySelectorAll(":scope > [data-slide]")]
      .sort((a, b) => Number(a.dataset.mikoMediaOrder) - Number(b.dataset.mikoMediaOrder));
  }
  const wanted = select.value;
  const slides = slider._mikoAllSlides.filter((slide) => wanted === "all" || slide.dataset.mikoModelFilter === wanted);
  if (!slides.length) return;
  try {
    const { Flickity } = await import(window.flu.chunks.flickity);
    const instance = Flickity.data(slider);
    if (instance) {
      const current = instance.getCellElements();
      if (current.length) instance.remove(current);
      const pagination = slider.querySelector(".mobile-carousel__pagination");
      slides.forEach((slide) => {
        slide.hidden = false;
        slider.insertBefore(slide, pagination || null);
      });
      instance.reloadCells();
      instance.resize();
      instance.select(0, false, true);
    } else {
      slider._mikoAllSlides.forEach((slide) => { slide.hidden = !slides.includes(slide); });
    }
  } catch (_) {
    slider._mikoAllSlides.forEach((slide) => { slide.hidden = !slides.includes(slide); });
  }
}

document.addEventListener("change", (event) => {
  const select = event.target.closest("[data-miko-media-filter-select]");
  if (select) mikoFilterGallery(select);
});

function mikoInit(root = document) {
  let saved = "model";
  try { saved = localStorage.getItem(MIKO_VIEW_KEY) || "model"; } catch (_) {}
  mikoApplyViewMode(saved);
  mikoInitBundles(root);
  mikoInitSizeAvailability(root);
  root.querySelectorAll('select[name^="options["]').forEach(mikoUpdateRegularSizing);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => mikoInit());
} else {
  mikoInit();
}
document.addEventListener("quickview:loaded", () => mikoInit());
document.addEventListener("shopify:section:load", (event) => mikoInit(event.target));
new MutationObserver(() => mikoApplyViewMode(document.documentElement.dataset.mikoViewMode || "model"))
  .observe(document.documentElement, { childList: true, subtree: true });


// On touch devices, the first image tap reveals sizes and the second product image.
// A second tap follows the product link, matching hover behaviour without trapping navigation.
function mikoCloseTouchCards(except = null) {
  document.querySelectorAll("[data-miko-product-media].is-touch-active").forEach((media) => {
    if (media !== except) media.classList.remove("is-touch-active");
  });
}

document.addEventListener("click", (event) => {
  const imageLink = event.target.closest(".product-item__image-link");
  const supportsTouchReveal = window.matchMedia("(hover: none), (pointer: coarse)").matches;

  if (imageLink && supportsTouchReveal) {
    const media = imageLink.closest("[data-miko-product-media]");
    if (media && !media.classList.contains("is-touch-active")) {
      event.preventDefault();
      mikoCloseTouchCards(media);
      media.classList.add("is-touch-active");
      return;
    }
  }

  if (!event.target.closest("[data-miko-product-card]")) {
    mikoCloseTouchCards();
  }
});
